import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Fab,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Psychology as AIIcon, // Changed from Smart to Psychology
  PlayArrow as RunIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CloneIcon,
  Settings as SettingsIcon,
  Group as TeamIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import FullscreenButton from './FullscreenButton';

function AgentManager() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [newAgent, setNewAgent] = useState({
    name: '',
    description: '',
    type: 'assistant',
    visibility: 'private',
  });

  // Mock agent data
  useEffect(() => {
    const mockAgents = [
      {
        id: '1',
        name: 'Research Assistant',
        description: 'Helps with research tasks and data analysis',
        type: 'research',
        status: 'active',
        visibility: 'private',
        lastActive: new Date(),
        conversations: 45,
        rating: 4.8,
        isFavorite: true,
        avatar: null,
      },
      {
        id: '2',
        name: 'Code Helper',
        description: 'Assists with programming and debugging',
        type: 'coding',
        status: 'active',
        visibility: 'public',
        lastActive: new Date(Date.now() - 3600000),
        conversations: 32,
        rating: 4.6,
        isFavorite: false,
        avatar: null,
      },
      {
        id: '3',
        name: 'Content Writer',
        description: 'Creates and edits written content',
        type: 'writing',
        status: 'draft',
        visibility: 'private',
        lastActive: new Date(Date.now() - 86400000),
        conversations: 12,
        rating: 4.2,
        isFavorite: true,
        avatar: null,
      },
    ];

    setTimeout(() => {
      setAgents(mockAgents);
      setLoading(false);
    }, 1000);
  }, []);

  const handleCreateAgent = () => {
    const agent = {
      id: Date.now().toString(),
      ...newAgent,
      status: 'draft',
      lastActive: new Date(),
      conversations: 0,
      rating: 0,
      isFavorite: false,
      avatar: null,
    };

    setAgents((prev) => [agent, ...prev]);
    setCreateDialogOpen(false);
    setNewAgent({
      name: '',
      description: '',
      type: 'assistant',
      visibility: 'private',
    });
  };

  const handleMenuOpen = (event, agent) => {
    setMenuAnchor(event.currentTarget);
    setSelectedAgent(agent);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedAgent(null);
  };

  const handleToggleFavorite = (agentId) => {
    setAgents((prev) => prev.map((agent) => (agent.id === agentId
      ? { ...agent, isFavorite: !agent.isFavorite }
      : agent)));
  };

  const handleDeleteAgent = (agentId) => {
    setAgents((prev) => prev.filter((agent) => agent.id !== agentId));
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'research': return '🔬';
      case 'coding': return '💻';
      case 'writing': return '✍️';
      case 'assistant': return '🤖';
      default: return '🤖';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Loading Agents...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h4" component="h1">
          Agent Manager
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FullscreenButton />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Agent
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Agents
              </Typography>
              <Typography variant="h4">
                {agents.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Agents
              </Typography>
              <Typography variant="h4">
                {agents.filter((a) => a.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Conversations
              </Typography>
              <Typography variant="h4">
                {agents.reduce((sum, a) => sum + a.conversations, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Rating
              </Typography>
              <Typography variant="h4">
                {agents.length > 0
                  ? (agents.reduce((sum, a) => sum + a.rating, 0) / agents.length).toFixed(1)
                  : '0.0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Agents Grid */}
      <Grid container spacing={3}>
        {agents.map((agent) => (
          <Grid item xs={12} sm={6} md={4} key={agent.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2,
                }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" component="h2">
                      {getTypeIcon(agent.type)}
                      {' '}
                      {agent.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleFavorite(agent.id)}
                    >
                      {agent.isFavorite ? <StarIcon color="warning" /> : <StarBorderIcon />}
                    </IconButton>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, agent)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {agent.description}
                </Typography>

                <Box sx={{
                  display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap',
                }}
                >
                  <Chip
                    label={agent.status}
                    color={getStatusColor(agent.status)}
                    size="small"
                  />
                  <Chip
                    label={agent.visibility}
                    icon={agent.visibility === 'public' ? <PublicIcon /> : <PrivateIcon />}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {agent.conversations}
                  {' '}
                  conversations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rating:
                  {' '}
                  {agent.rating}
                  /5.0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last active:
                  {' '}
                  {agent.lastActive.toLocaleDateString()}
                </Typography>
              </CardContent>

              <CardActions>
                <Button size="small" startIcon={<RunIcon />}>
                  Run
                </Button>
                <Button size="small" startIcon={<EditIcon />}>
                  Edit
                </Button>
                <Button size="small" startIcon={<SettingsIcon />}>
                  Configure
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Agent
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <CloneIcon fontSize="small" />
          </ListItemIcon>
          Clone Agent
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={() => selectedAgent && handleDeleteAgent(selectedAgent.id)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete Agent
        </MenuItem>
      </Menu>

      {/* Create Agent Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Agent Name"
              fullWidth
              variant="outlined"
              value={newAgent.name}
              onChange={(e) => setNewAgent((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={newAgent.description}
              onChange={(e) => setNewAgent((prev) => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Type"
              fullWidth
              select
              variant="outlined"
              value={newAgent.type}
              onChange={(e) => setNewAgent((prev) => ({ ...prev, type: e.target.value }))}
              sx={{ mb: 2 }}
            >
              <MenuItem value="assistant">Assistant</MenuItem>
              <MenuItem value="research">Research</MenuItem>
              <MenuItem value="coding">Coding</MenuItem>
              <MenuItem value="writing">Writing</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              label="Visibility"
              fullWidth
              select
              variant="outlined"
              value={newAgent.visibility}
              onChange={(e) => setNewAgent((prev) => ({ ...prev, visibility: e.target.value }))}
            >
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="public">Public</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateAgent} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AgentManager;
